/**
 * War and peace.
 *
 * Phase 7a's diplomacy is deliberately the smallest thing that makes war a decision:
 * everyone starts at peace, war is declared — by the player confirming an attack, or
 * by an AI that finds itself much stronger — and peace is offered and accepted or
 * refused. Treaties, trade, attitudes and memory of grievances are Phase 10, and they
 * extend the Relation record rather than replacing it.
 *
 * The rules about WHEN an AI wants peace live here rather than in the AI module,
 * because the player's offers are answered by them too: an AI that refused your peace
 * should refuse it for the same reasons it would decline to offer its own.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { NONE, type GameState, type Id } from '../state/types.js';
import { ensureRelation, relationOf } from './relation.js';
import { remember } from './memory.js';
import { allUnits, unitDef } from '../units/index.js';
import { weightsFor } from '../ai/personality.js';
import { addWeariness, wearyCitizens } from '../government/index.js';

export { ensureRelation, relationOf } from './relation.js';

export function atWar(state: GameState, x: Id, y: Id): boolean {
  if (x === y) return false;
  return relationOf(state, x, y)?.war === true;
}

/** Civilisations this one is at war with, ascending. */
export function enemiesOf(state: GameState, civId: Id): Id[] {
  const out: Id[] = [];
  for (const r of state.relations) {
    if (!r.war) continue;
    if (r.a === civId) out.push(r.b);
    else if (r.b === civId) out.push(r.a);
  }
  return out.sort((p, q) => p - q);
}

/** Civilisations still in the game, other than this one, ascending. */
export function rivalsOf(state: GameState, civId: Id): Id[] {
  return Object.values(state.civs)
    .filter((c) => c.id !== civId && !c.defeated)
    .map((c) => c.id)
    .sort((p, q) => p - q);
}

/** Turns of truce left before `civId` may declare war on `other`. Zero when free to. */
export function truceLeft(state: GameState, civId: Id, other: Id): number {
  const relation = relationOf(state, civId, other);
  return relation ? Math.max(0, relation.truceUntil - state.meta.turn) : 0;
}

/**
 * War. The victim remembers; open borders close; payments between the two stop (a war
 * is not a default). Breaking a treaty is judged by the command, which knows the rules.
 */
export function declareWar(state: GameState, by: Id, on: Id): GameEvent[] {
  const relation = ensureRelation(state, by, on);
  relation.war = true;
  relation.since = state.meta.turn;
  relation.lossesA = 0;
  relation.lossesB = 0;
  relation.peaceOfferedBy = NONE;
  relation.peaceGive = [];
  relation.peaceGet = [];
  relation.campaignA = null;
  relation.campaignB = null;
  relation.openBorders = false;
  relation.treatyUntil = 0;
  state.payments = state.payments.filter((p) => !((p.from === by && p.to === on) || (p.from === on && p.to === by)));
  state.grants = state.grants.filter((g) => !((g.from === by && g.to === on) || (g.from === on && g.to === by)));
  remember(state, on, by, 'warDeclared');
  return [{ t: 'WarDeclared', by, on }];
}

/** The turns of treaty left between two civilisations, after the truce. Zero when none. */
export function treatyLeft(state: GameState, x: Id, y: Id): number {
  const relation = relationOf(state, x, y);
  return relation && !relation.war ? Math.max(0, relation.treatyUntil - state.meta.turn) : 0;
}

/**
 * Peace, and a treaty: no war at all during the truce, and war before the treaty ends
 * breaks it (ADR-0060). An ally still fighting the same enemy remembers being left to
 * fight alone, and that alliance is over.
 */
export function makePeace(state: GameState, content: Content, x: Id, y: Id): GameEvent[] {
  const relation = ensureRelation(state, x, y);
  relation.war = false;
  relation.since = state.meta.turn;
  relation.truceUntil = state.meta.turn + content.rules.diplomacy.truceTurns;
  relation.treatyUntil = state.meta.turn + content.rules.diplomacy.treatyTurns;
  relation.peaceOfferedBy = NONE;
  relation.peaceGive = [];
  relation.peaceGet = [];
  relation.campaignA = null;
  relation.campaignB = null;
  remember(state, x, y, 'peaceMade');
  remember(state, y, x, 'peaceMade');
  const events: GameEvent[] = [{ t: 'PeaceMade', a: relation.a, b: relation.b }];
  for (const [side, enemy] of [
    [x, y],
    [y, x],
  ] as const) {
    for (const pact of state.relations) {
      if (pact.a !== side && pact.b !== side) continue;
      if (!pact.alliances.includes(enemy)) continue;
      const ally = pact.a === side ? pact.b : pact.a;
      pact.alliances = pact.alliances.filter((id) => id !== enemy);
      if (atWar(state, ally, enemy)) remember(state, ally, side, 'deserted');
      events.push({ t: 'AllianceEnded', a: side, b: ally, against: enemy });
    }
  }
  return events;
}

/** Charge a loss to one side of a war, in shields. Ignored when the two are not at war. */
export function recordLoss(state: GameState, loser: Id, winner: Id, value: number): void {
  const relation = relationOf(state, loser, winner);
  if (!relation || !relation.war) return;
  if (relation.a === loser) relation.lossesA += value;
  else relation.lossesB += value;
  // The people count the cost of a war too (ADR-0059).
  addWeariness(state, loser, value);
}

/** What a civilisation has lost, and what it has inflicted, in its war with another. */
export function warLosses(state: GameState, civId: Id, enemy: Id): { lost: number; inflicted: number } {
  const relation = relationOf(state, civId, enemy);
  if (!relation) return { lost: 0, inflicted: 0 };
  return relation.a === civId
    ? { lost: relation.lossesA, inflicted: relation.lossesB }
    : { lost: relation.lossesB, inflicted: relation.lossesA };
}

/**
 * A civilisation's military strength: every unit that can fight, by the average of
 * its attack and defence, scaled by the health it has left.
 *
 * Public knowledge, not a peek through the fog: any civilisation's envoys can count
 * soldiers in the streets, and the player is shown the same comparison the AI uses.
 * A crude figure, and good enough to answer "would we win?" — the question AI-2 will
 * answer properly with what each side can actually bring to the front.
 */
export function militaryStrength(state: GameState, content: Content, civId: Id): number {
  let total = 0;
  for (const unit of allUnits(state)) {
    if (unit.owner !== civId) continue;
    const def = unitDef(content, unit);
    if (def.attack === 0 && def.defence === 0) continue;
    total += ((def.attack + def.defence) / 2) * (unit.hp / def.hp);
  }
  return total;
}

/** Fighting units — anything that can attack or defend. */
export function fightingUnits(state: GameState, content: Content, civId: Id): number {
  let count = 0;
  for (const unit of allUnits(state)) {
    if (unit.owner !== civId) continue;
    const def = unitDef(content, unit);
    if (def.attack > 0 || def.defence > 0) count++;
  }
  return count;
}

/** Why a civilisation would rather stop fighting (ADR-0071). */
export type PeaceReason = 'aimMet' | 'losing' | 'outmatched' | 'weary' | 'stalemate';

/**
 * Why an AI civilisation would make peace now, or null while it means to fight on.
 *
 * A war ends when it has done what it was for (its plan's aim cities are taken), when
 * it is clearly being lost (`peaceLossRatio` times what it has inflicted, and at least
 * `peaceLossFloor` shields, with its army not on the march), when the enemy has grown stronger
 * (`peaceWhenStrengthBelow`), when the people tire of it (`peaceWearyCitizens`
 * discontented in every city), or when nothing has been taken for `stalemateTurns`.
 * Not on a timer: a war that is going well goes on (ADR-0071).
 */
export function peaceReason(state: GameState, content: Content, civId: Id, enemy: Id): PeaceReason | null {
  const relation = relationOf(state, civId, enemy);
  if (!relation || !relation.war) return null;
  const ai = weightsFor(content, state.civs[civId]);
  const campaign = relation.a === civId ? relation.campaignA : relation.campaignB;
  if (campaign && campaign.taken >= campaign.goal) return 'aimMet';
  // An army on the march takes losses at the walls before a city falls; the war is
  // lost when the army breaks and falls back, not with its first casualties.
  const { lost, inflicted } = warLosses(state, civId, enemy);
  if (!campaign?.marching && lost >= ai.peaceLossFloor && lost > inflicted * ai.peaceLossRatio) return 'losing';
  // An army on the march is more stubborn (ADR-0072): it gives up only when clearly
  // weaker (`marchResolve` of the usual line) and bears one more weary citizen.
  const marching = campaign?.marching === true;
  const ours = militaryStrength(state, content, civId);
  const theirs = militaryStrength(state, content, enemy);
  if (ours < theirs * ai.peaceWhenStrengthBelow * (marching ? ai.marchResolve : 1)) return 'outmatched';
  if (wearyCitizens(state, content, civId) >= ai.peaceWearyCitizens + (marching ? 1 : 0)) return 'weary';
  const lastGain = campaign ? campaign.lastGain : relation.since;
  if (state.meta.turn - lastGain >= ai.stalemateTurns) return 'stalemate';
  return null;
}

/**
 * Whether an AI civilisation would rather stop fighting: a reason to (`peaceReason`),
 * and never in the first `minimumWarTurns` — a war declared and abandoned in the same
 * breath is not a war.
 */
export function wantsPeace(state: GameState, content: Content, civId: Id, enemy: Id): boolean {
  const relation = relationOf(state, civId, enemy);
  if (!relation || !relation.war) return false;
  const ai = weightsFor(content, state.civs[civId]);
  if (state.meta.turn - relation.since < ai.minimumWarTurns) return false;
  return peaceReason(state, content, civId, enemy) !== null;
}

/** Turns before this side may offer peace again. Zero when it may now. */
export function offerCooldown(state: GameState, content: Content, civId: Id, enemy: Id): number {
  const relation = relationOf(state, civId, enemy);
  if (!relation) return 0;
  const last = relation.a === civId ? relation.lastOfferA : relation.lastOfferB;
  if (last < 0) return 0;
  return Math.max(0, last + content.rules.diplomacy.peaceOfferCooldown - state.meta.turn);
}

export function noteOffer(state: GameState, civId: Id, enemy: Id): void {
  const relation = ensureRelation(state, civId, enemy);
  if (relation.a === civId) relation.lastOfferA = state.meta.turn;
  else relation.lastOfferB = state.meta.turn;
}
